import { useMutation } from '@apollo/client'
import {
    Edit as EditIcon,
    KeyboardArrowLeft,
    KeyboardArrowRight,
    PushPin,
    PushPinOutlined,
} from '@mui/icons-material'
import {
    Box,
    Button,
    Divider,
    IconButton,
    MobileStepper,
    Stack,
    Typography,
} from '@mui/material'
import StarterKit from '@tiptap/starter-kit'
import { RichTextReadOnly } from 'mui-tiptap'
import React, { useEffect, useState } from 'react'
import { ProfilePanel } from 'recruiter/components/ProfilePanel'
import { PinCompanyNote } from 'recruiter/graphql/companyNotes.gql'
import theme from 'recruiter/theme'
import useToast from 'shared/components/Toast/useToast'
import useIsMobile from 'shared/hooks/useIsMobile'
import { getPxAsRem } from 'shared/utils'
import { useNotesContext } from 'shared/utils/noteContext'

const PinnedNotes = ({ notes, refetch }) => {
    const pinnedNotes = notes.filter((n) => n.isPinned && n.isActive)
    const [currentSlide, setCurrentSlide] = React.useState(0)
    const [activeStep, setActiveStep] = useState(0)
    const { openEditNote } = useNotesContext()

    const isMobile = useIsMobile()
    const [pinCompanyNote] = useMutation(PinCompanyNote)

    const toast = useToast()

    useEffect(() => {
        if (pinnedNotes.length === 0) {
            setCurrentSlide(0)
        } else if (currentSlide >= pinnedNotes.length) {
            setCurrentSlide(pinnedNotes.length - 1)
        }
    }, [pinnedNotes.length, currentSlide])

    if (pinnedNotes.length === 0) {
        return
    }

    const itemsPerPage = 3

    const totalSteps = Math.ceil(pinnedNotes.length / itemsPerPage)

    const handleNext = () => {
        setActiveStep((prevActiveStep) => prevActiveStep + 1)
    }

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1)
    }

    const startIndex = activeStep * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    const visibleNotes = pinnedNotes.slice(startIndex, endIndex)

    return (
        <ProfilePanel
            panelTitle="Pinned Notes"
            sx={{ height: getPxAsRem(300) }}
        >
            <Stack
                direction="row"
                gap={getPxAsRem(16)}
                height={getPxAsRem(220)}
            >
                {visibleNotes.map((note, index) => {
                    const {
                        updatedAt,
                        noteData,
                        updatedByUser,
                        createdByUser,
                        title,
                        id,
                    } = note
                    const isFirstOnPage = index % 3 !== 0
                    const dividerCount = visibleNotes.length - 1
                    const pinnedNotesSpacingPixel = 8
                    const spaceBetweenPixels =
                        dividerCount *
                        (pinnedNotesSpacingPixel * 2 + dividerCount)
                    const pinNote = async (noteId) => {
                        await pinCompanyNote({
                            variables: { noteId },
                            onCompleted: () => refetch(),
                            onError: (error) => {
                                console.error(error)
                                toast({
                                    title: 'Error!',
                                    message:
                                        'Something went wrong. Please try again later.',
                                    severity: 'error',
                                })
                            },
                        })
                    }

                    return (
                        <React.Fragment key={id}>
                            {isFirstOnPage && (
                                <Divider orientation="vertical" flexItem />
                            )}
                            <Box
                                sx={{
                                    flex: '1 1 0px',
                                    maxWidth: `calc((100% - ${spaceBetweenPixels}px) / ${visibleNotes.length})`,
                                }}
                            >
                                <Box
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                >
                                    <Typography
                                        variant="h6"
                                        component="div"
                                        fontWeight="bold"
                                        title={title}
                                        sx={{
                                            flexWrap: 'nowrap',
                                            textOverflow: 'ellipsis',
                                            overflow: 'hidden',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {title}
                                    </Typography>
                                    {isMobile ? (
                                        <PushPin color="action" />
                                    ) : (
                                        <Box
                                            flexWrap="nowrap"
                                            flexShrink={0}
                                            display="flex"
                                        >
                                            <IconButton
                                                onClick={() =>
                                                    openEditNote(note)
                                                }
                                            >
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton
                                                onClick={() => pinNote(id)}
                                            >
                                                {note.isPinned ? (
                                                    <PushPin />
                                                ) : (
                                                    <PushPinOutlined />
                                                )}
                                            </IconButton>
                                        </Box>
                                    )}
                                </Box>
                                <Typography
                                    sx={{ mb: 1.5 }}
                                    color="text.secondary"
                                >
                                    {new Date(updatedAt).toLocaleDateString()} •{' '}
                                    {new Date(updatedAt).toLocaleTimeString()}
                                </Typography>
                                <Typography variant="body2">
                                    {updatedByUser
                                        ? `${updatedByUser.firstName} ${updatedByUser.lastName}`
                                        : `${createdByUser.firstName} ${createdByUser.lastName}`}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    component="div"
                                    sx={{
                                        mt: 1,
                                        display: '-webkit-box',
                                        overflow: 'hidden',
                                        WebkitBoxOrient: 'vertical',
                                        WebkitLineClamp: 3,
                                    }}
                                >
                                    <RichTextReadOnly
                                        extensions={[StarterKit]}
                                        content={noteData}
                                    />
                                </Typography>
                            </Box>
                        </React.Fragment>
                    )
                })}
            </Stack>
            <MobileStepper
                variant="dots"
                steps={totalSteps}
                position="static"
                activeStep={activeStep}
                sx={{
                    flexGrow: 1,
                    background: 'transparent',
                    boxShadow: 'none',
                    padding: 0,
                }}
                nextButton={
                    <Button
                        size="small"
                        variant="text"
                        onClick={handleNext}
                        disabled={activeStep === totalSteps - 1}
                    >
                        Next
                        {theme.direction === 'rtl' ? (
                            <KeyboardArrowLeft />
                        ) : (
                            <KeyboardArrowRight />
                        )}
                    </Button>
                }
                backButton={
                    <Button
                        size="small"
                        variant="text"
                        onClick={handleBack}
                        disabled={activeStep === 0}
                    >
                        {theme.direction === 'rtl' ? (
                            <KeyboardArrowRight />
                        ) : (
                            <KeyboardArrowLeft />
                        )}
                        Back
                    </Button>
                }
            />
        </ProfilePanel>
    )
}

export default PinnedNotes
